import React from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Circle,
  useBreakpointValue,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';

/**
 * Timeline Component - Displays the council formation process timeline
 * 
 * CONTROLS:
 * 1. Update Timeline Items:
 *    - Modify the `timelineData` array to add, remove, or update timeline items
 *    - Each item requires:
 *      - id: Unique identifier (number)
 *      - title: The stage title in Arabic
 *      - date: Date range in Arabic (can be empty string if not applicable)
 *      - isCompleted: Boolean indicating if the stage is completed
 *
 * 2. Styling:
 *    - Completed items: Brown background with white text
 *    - Pending items: Gray outline with gray text
 *    - Hover effects: Subtle shadow and translation on hover
 *    - Responsive design: Adjusts for mobile and desktop views
 *
 * 3. Date Format:
 *    - Use Arabic numerals and Arabic month names
 *    - Example: '٢٠ يوليو - ٤ سبتمبر ٢٠٢٥م'
 *
 * 4. Last Updated Date:
 *    - Update the date in the footer by modifying the text at the bottom of the component
 *
 * 5. Colors:
 *    - Uses the Majalisna theme colors (brown, teal, beige, mint)
 *    - Modify colors in the theme file if needed
 */

/**
 * Timeline page component
 * A vertical timeline showing the stages of council formation process
 */
const Timeline = () => {
  // Timeline data with stages and optional dates
  const timelineData = [
    {
      id: 1,
      title: 'تهيئة أعضاء المجلس',
      date: '',
      isCompleted: true,
    },
    {
      id: 2,
      title: 'إعداد اللوائح',
      date: '',
      isCompleted: true,
    },
    {
      id: 3,
      title: 'إعداد تقرير تقييم المراكز',
      date: '',
      isCompleted: true,
    },
    {
      id: 4,
      title: 'مناقشة تقييم المراكز ومراجعته',
      date: '',
      isCompleted: true,
    },
    {
      id: 5,
      title: 'طلب إصدار خطاب تشكيل أعضاء مجلس الإدارة للدورة الخامسة',
      date: '',
      isCompleted: true,
    },
    {
      id: 6,
      title: 'شكر أعضاء مجالس الأحياء في الدورة الرابعة',
      date: '',
      isCompleted: true,
    },
    {
      id: 7,
      title: 'طلب الموافقة والاعتماد',
      date: '',
      isCompleted: true,
    },
    {
      id: 8,
      title: 'الدعوة للانضمام لمجلس مركز الحي',
      date: '',
      isCompleted: true,
    },
    {
      id: 9,
      title: 'عقد اللقاءات التعريفية',
      date: '١٠ أغسطس - ٤ سبتمبر ٢٠٢٥م',
      isCompleted: true,
    },
    {
      id: 10,
      title: 'استقبال الطلبات',
      date: '١٠ أغسطس - ٤ سبتمبر ٢٠٢٥م',
      isCompleted: true,
    },
    {
      id: 11,
      title: 'الفرز والترشيح للتعيين',
      date: '٢١ سبتمبر - ٢٦ نوفمبر ٢٠٢٥م',
      isCompleted: false,
    },
    {
      id: 12,
      title: 'إصدار قرار تكوين أعضاء المجلس',
      date: '',
      isCompleted: false,
    },
    {
      id: 13,
      title: 'تشكيل المجلس',
      date: '',
      isCompleted: false,
    },
  ];

  // Responsive design adjustments
  const circleSize = useBreakpointValue({ base: '40px', md: '48px' });
  const lineHeight = useBreakpointValue({ base: '55px', md: '70px' });
  const spacing = useBreakpointValue({ base: 4, md: 6 });
  
  // Colors from theme
  const completedColor = 'brown.500';
  const pendingColor = 'gray.300';
  const bgColor = useColorModeValue('white', 'gray.800');

  return (
    <Container maxW="container.lg" py={8}>
      <Heading textAlign="center" mb={8} color="brown.700">
        الخط الزمني لتشكيل مجالس مراكز الأحياء
      </Heading>
      
      <Box 
        bg={bgColor} 
        borderRadius="xl" 
        boxShadow="md" 
        p={{ base: 4, md: 6 }}
        position="relative"
        overflow="hidden"
      >
        <VStack spacing={0} align="stretch" position="relative">
          {timelineData.map((item, index) => {
            const isLast = index === timelineData.length - 1;
            const statusColor = item.isCompleted ? completedColor : pendingColor;
            
            return (
              <Box key={item.id} position="relative">
                <HStack spacing={spacing} align="flex-start">
                  {/* Timeline node */}
                  <VStack spacing={0} align="center" flexShrink={0}>
                    <Circle 
                      size={circleSize} 
                      bg={item.isCompleted ? completedColor : 'white'}
                      border="2px solid"
                      borderColor={statusColor}
                      color={item.isCompleted ? 'white' : statusColor}
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {item.id}
                    </Circle>
                    
                    {/* Vertical line */}
                    {!isLast && (
                      <Box 
                        w="2px" 
                        h={lineHeight} 
                        bg={statusColor} 
                        my={1}
                      />
                    )}
                  </VStack>
                  
                  {/* Content */}
                  <Box 
                    p={4} 
                    bg={item.isCompleted ? 'teal.50' : 'gray.50'}
                    borderRadius="md"
                    borderRight="4px solid"
                    borderColor={statusColor}
                    mb={isLast ? 0 : 4}
                    w="100%"
                    transition="all 0.3s"
                    _hover={{ transform: 'translateX(-5px)', boxShadow: 'sm' }}
                  >
                    <Text fontWeight="bold" fontSize="lg" color="brown.800">
                      {item.title}
                    </Text>
                    
                    {item.date && (
                      <Badge 
                        colorScheme={item.isCompleted ? 'teal' : 'gray'}
                        mt={2}
                        px={2}
                        py={1}
                        borderRadius="full"
                      >
                        {item.date}
                      </Badge>
                    )}
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
        
        {/* Background decoration */}
        <Box 
          position="absolute"
          top="0"
          right="0"
          w="200px"
          h="200px"
          borderRadius="full"
          bg="mint.100"
          opacity="0.3"
          transform="translate(50%, -50%)"
          zIndex="0"
        />
        
        <Box 
          position="absolute"
          bottom="0"
          left="0"
          w="150px"
          h="150px"
          borderRadius="full"
          bg="beige.100"
          opacity="0.3"
          transform="translate(-30%, 30%)"
          zIndex="0"
        />
      </Box>
      
      <Box textAlign="center" mt={6} color="gray.600">
        <Text fontSize="sm">
          تم التحديث بتاريخ: 12 أغسطس 2025 م
        </Text>
      </Box>
    </Container>
  );
};

export default Timeline;
